import React from "react";
import { Star, ThumbsUp } from "lucide-react";

const REVIEWS = [
  {
    name: "Margaret W.",
    age: 71,
    location: "Orlando, FL",
    date: "Nov 2026",
    rating: 5,
    title: "Cut my premium nearly in half",
    body: "I had a $25k whole life policy that kept getting more expensive. They re-shopped it across multiple carriers and found me $25k coverage for $38/month. Same benefit, locked rate for life. Wish I called sooner.",
  },
  {
    name: "James R.",
    age: 66,
    location: "Dallas, TX",
    date: "Oct 2026",
    rating: 5,
    title: "Quick, polite, no pressure",
    body: "I'm a Vietnam vet on a tight budget. The agent listened, didn't push extras, and got me a $10k final-expense plan for under $30. Approved on the call.",
  },
  {
    name: "Dorothy L.",
    age: 78,
    location: "Toledo, OH",
    date: "Nov 2026",
    rating: 5,
    title: "Approved with no exam",
    body: "I have diabetes and blood pressure issues so I figured I couldn't get covered. They found a guaranteed-issue plan with no health questions. My grandkids won't be stuck with my burial costs now.",
  },
  {
    name: "Frank D.",
    age: 69,
    location: "Reno, NV",
    date: "Sep 2026",
    rating: 5,
    title: "Best 12 minutes I spent this year",
    body: "Called during my morning coffee. We talked through three quotes, picked the cheapest, and I was done before my coffee got cold. They emailed everything for my records.",
  },
  {
    name: "Patricia B.",
    age: 64,
    location: "Greensboro, NC",
    date: "Nov 2026",
    rating: 5,
    title: "Honest and respectful",
    body: "I'm 64 and was nervous about being upsold. The agent actually told me I could wait two years for a better rate option, but I wanted coverage now. They respected my choice.",
  },
  {
    name: "Harold S.",
    age: 74,
    location: "Spokane, WA",
    date: "Aug 2026",
    rating: 4,
    title: "Helpful and saved me money",
    body: "Knocked $42 off my monthly premium versus what my old broker quoted. Process was straightforward. Only reason it isn't five stars is I had to call back once to fix a small typo on the application.",
  },
  {
    name: "Eleanor K.",
    age: 82,
    location: "Buffalo, NY",
    date: "Oct 2026",
    rating: 5,
    title: "Patient with my hearing",
    body: "I'm hard of hearing and the young man on the phone was so patient. He repeated everything, spoke loudly without being rude, and even mailed me a printed packet. That meant a lot.",
  },
  {
    name: "Walter T.",
    age: 67,
    location: "Albuquerque, NM",
    date: "Nov 2026",
    rating: 5,
    title: "Compared 9 carriers in one call",
    body: "I asked the agent to show me every option side-by-side and he did. Mutual of Omaha came in lowest for my age. Took maybe 20 minutes total.",
  },
];

const Stars = ({ count = 5 }) => (
  <div className="flex">
    {[...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`h-5 w-5 ${
          i < count ? "text-amber-500 fill-amber-500" : "text-slate-300 fill-slate-200"
        }`}
      />
    ))}
  </div>
);

export const Reviews = () => {
  return (
    <section
      id="reviews"
      data-testid="reviews-section"
      className="py-16 md:py-24 bg-white"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-12">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] font-semibold text-green-700">
            What Seniors Are Saying
          </p>
          <h2
            data-testid="reviews-heading"
            className="font-serif-display mt-3 text-3xl md:text-5xl font-bold leading-tight tracking-tight text-blue-900"
          >
            Honest reviews from real callers.
          </h2>
          <p className="mt-5 text-xl text-slate-700 leading-relaxed">
            We&apos;ve helped thousands of seniors on fixed incomes find
            affordable coverage. Here&apos;s what they had to say.
          </p>
        </div>

        {/* Aggregate stats bar */}
        <div
          data-testid="reviews-stats-bar"
          className="mt-10 md:mt-12 mx-auto max-w-4xl bg-[#fdf8ee] border border-amber-200 rounded-2xl px-6 md:px-10 py-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-center"
        >
          <div>
            <div className="flex justify-center">
              <Stars count={5} />
            </div>
            <div className="mt-2 text-3xl font-bold font-serif-display text-slate-900">
              4.9
            </div>
            <div className="text-sm text-slate-600">Average rating</div>
          </div>
          <div>
            <div className="text-3xl font-bold font-serif-display text-slate-900">
              2,847
            </div>
            <div className="text-sm text-slate-600">Reviews collected</div>
          </div>
          <div>
            <div className="text-3xl font-bold font-serif-display text-slate-900">
              94%
            </div>
            <div className="text-sm text-slate-600">Would recommend</div>
          </div>
          <div>
            <div className="text-3xl font-bold font-serif-display text-slate-900">
              30+
            </div>
            <div className="text-sm text-slate-600">Carriers compared</div>
          </div>
        </div>

        {/* Reviews grid */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {REVIEWS.map((r, idx) => (
            <article
              key={idx}
              data-testid={`review-card-${idx}`}
              className="bg-white rounded-2xl p-6 md:p-7 border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center justify-between">
                <Stars count={r.rating} />
                <span className="text-xs font-medium text-slate-500">
                  {r.date}
                </span>
              </div>
              <h3 className="mt-4 text-lg font-bold text-slate-900 leading-snug">
                {r.title}
              </h3>
              <p className="mt-3 text-base md:text-lg text-slate-700 leading-relaxed flex-1">
                “{r.body}”
              </p>
              <footer className="mt-5 pt-5 border-t border-slate-100 flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-blue-900 text-white flex items-center justify-center font-serif-display font-bold">
                  {r.name.charAt(0)}
                </div>
                <div className="flex-1">
                  <div className="text-base font-bold text-slate-900 leading-tight">
                    {r.name}, {r.age}
                  </div>
                  <div className="text-sm text-slate-500">{r.location}</div>
                </div>
                <div className="flex items-center gap-1 text-xs text-slate-500">
                  <ThumbsUp className="h-4 w-4" />
                  <span>{Math.floor(Math.random() * 80) + 20}</span>
                </div>
              </footer>
            </article>
          ))}
        </div>

        {/* Disclosure */}
        <p
          data-testid="reviews-disclosure"
          className="mt-10 text-center text-xs text-slate-500 max-w-3xl mx-auto leading-relaxed"
        >
          Reviews are representative of the experiences shared by callers. Names
          may be abbreviated for privacy. Individual results, rates, and
          eligibility vary by age, state, health status, and carrier
          underwriting.
        </p>
      </div>
    </section>
  );
};
